import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';

// ---------------------------------------------------------------------------
// Types (mirror backend/src/modules/support responses)
// ---------------------------------------------------------------------------

export type SupportStatus = 'ok' | 'warning' | 'over';

export interface SupportSeriesPoint {
  month: string; // 'YYYY-MM'
  used: number;
  allowance: number | null;
  pct: number | null;
}

// --- Hour banks (the primary support model) --------------------------------

/** ok = healthy · low = at/below the first alert threshold · exhausted = empty
 *  · overage = work logged beyond every block. */
export type BankStatus = 'good' | 'ok' | 'low' | 'exhausted' | 'overage';

export interface BankExpiring {
  blockId: string;
  remaining: number;
  expiresAt: string;
  inDays: number;
}

/** The floor a bank pays every month, and where the number came from. */
export interface ManagementFeeRule {
  hours: number;
  exempt: boolean;
  source: 'default' | 'client';
}

export interface UnpaidBankInvoice {
  invoiceId: string;
  invoiceNumber: string;
  state: string;
  dueDate: string;
  dueAmount: number;
  /** Hours this invoice put in the bank, and how many of them are left. */
  hours: number;
  remaining: number;
}

export interface SupportBankRow {
  clientId: string;
  clientName: string;
  /**
   * An INACTIVE client goes quiet: no alert is recorded or emailed, no portal
   * link can be minted, and no minimum management fee is drawn. They stay on
   * this screen — an ex-client's remaining balance is exactly what somebody
   * looks up — but nothing leaves the building for them.
   */
  isActive: boolean;
  agreementIds: string[];
  alertThresholds: number[];
  defaultRate: number | null;
  balance: number;
  purchased: number;
  consumed: number;
  overage: number;
  expired: number;
  pctRemaining: number | null;
  status: BankStatus | null;
  bankStart: string | null;
  burnPerWeek: number;
  burnWindowDays: number;
  burnHours: number;
  /** The CLAMPED runway: the earlier of the pace draining the bank and its
   *  hours expiring. Expiring hours are gone whether or not anyone burns them,
   *  so a pace-only projection overstates it. */
  projectedExhaustion: string | null;
  daysToExhaustion: number | null;
  /** Which of the two ended it — so a screen can label the date honestly. */
  exhaustionReason: 'burn' | 'expiry' | null;
  /** The pace-only date, ignoring expiry. Context, never the headline. */
  paceExhaustion: string | null;
  blockCount: number;
  pendingHours: number;
  expiringSoon: BankExpiring[];
  /**
   * Hours counted in this bank whose invoice is sent but not paid. They are
   * not overage (they were billed) — the bank warns that the money is owed.
   */
  unpaidInvoices: UnpaidBankInvoice[];
  managementFee: ManagementFeeRule;
  /** Hours this bank has actually paid in fees, net of anything reversed. */
  managementFeeCharged: number;
  /** The most recent month a fee was drawn for ('YYYY-MM'), or null. */
  managementFeeLastPeriod: string | null;
}

export interface SupportBankSummary {
  clientCount: number;
  balance: number;
  purchased: number;
  consumed: number;
  overage: number;
  burnPerWeek: number;
  low: number;
  exhausted: number;
  inOverage: number;
  expiring: number;
}

export interface HourBlock {
  id: string;
  hours: number;
  rate: number;
  amount: number;
  purchasedAt: string;
  expiresAt: string | null;
  expiresInDays: number | null;
  source: 'INVOICE' | 'STRIPE' | 'MANUAL';
  status: 'PENDING' | 'ACTIVE' | 'EXHAUSTED' | 'EXPIRED' | 'REFUNDED';
  storedStatus: string;
  paid: boolean;
  consumed: number;
  remaining: number;
  expired: number;
  note: string | null;
  /** The agreement these hours were sold against, or Harvest-derived from. Null = the client's pool. */
  agreementId: string | null;
  agreementName: string | null;
  invoiceId: string | null;
  invoiceNumber: string | null;
  invoiceState: string | null;
}

export interface HourLedgerRow {
  blockId: string | null;
  timeEntryId: string | null;
  delta: number;
  balanceAfter: number;
  reason:
    | 'purchase'
    | 'consumption'
    | 'overage'
    | 'expiry'
    | 'refund'
    | 'adjustment'
    | 'management_fee'
    | 'management_fee_reversal';
  occurredAt: string;
  note: string | null;
  /** 'YYYY-MM' on a management fee and on the fact that reverses it. */
  periodKey: string | null;
}

export interface HourAlertRow {
  id: string;
  clientId?: string;
  clientName?: string;
  threshold: number;
  balanceHours: number;
  purchasedHours: number;
  channel: string;
  sentAt: string;
  acknowledgedAt: string | null;
}

/**
 * An invoice line that SOLD this client hours which never reached the bank —
 * a hand-built sale with no hour block behind it. Named beside the balance so
 * an overage it causes points at the invoice (Compulink #26-0003).
 */
export interface UnbankedSale {
  invoiceId: string;
  invoiceNumber: string;
  invoiceState: string;
  issueDate: string;
  lineId: string;
  description: string;
  hours: number;
  rate: number;
  amount: number;
  agreement: { id: string; name: string } | null;
}

export interface SupportBankDetail extends SupportBankRow {
  blocks: HourBlock[];
  ledger: HourLedgerRow[];
  alerts: HourAlertRow[];
  unbankedSales: UnbankedSale[];
}

export interface SupportClientRow {
  clientId: string;
  clientName: string;
  allowance: number | null;
  used: number;
  remaining: number | null;
  overage: number | null;
  pct: number | null;
  status: SupportStatus | null;
  agreementIds: string[];
  series: SupportSeriesPoint[];
  /** null = this client has no bank, only (maybe) a monthly allowance. */
  bank: SupportBankRow | null;
}

export interface SupportReport {
  month: string;
  summary: {
    clientCount: number;
    totalAllowance: number;
    totalUsed: number;
    overCount: number;
    bankClientCount: number;
    totalBalance: number;
    totalOverage: number;
    totalBurnPerWeek: number;
    lowCount: number;
    exhaustedCount: number;
    overageCount: number;
    expiringCount: number;
  };
  clients: SupportClientRow[];
}

export interface SupportEntry {
  id: string;
  date: string;
  personName: string;
  taskName: string | null;
  agreementName: string;
  hours: number;
  billable: boolean;
  notes: string | null;
}

export interface SupportClientDetail {
  clientId: string;
  clientName: string;
  month: string;
  allowance: number | null;
  used: number;
  remaining: number | null;
  overage: number | null;
  pct: number | null;
  status: SupportStatus | null;
  people: Array<{ personId: string; name: string; hours: number }>;
  entries: SupportEntry[];
}

export interface Allowance {
  id: string;
  clientId: string;
  monthlyHours: number;
  effectiveFrom: string;
  effectiveTo: string | null;
}

export interface AllowanceClient {
  clientId: string;
  clientName: string;
  current: Allowance | null;
  history: Allowance[];
}

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

export function useSupportReport(month: string) {
  return useQuery({
    queryKey: ['support', 'report', month],
    queryFn: () => api.get<SupportReport>('/support', { params: { month } }),
    placeholderData: keepPreviousData,
  });
}

export function useSupportClientDetail(clientId: string | null, month: string) {
  return useQuery({
    queryKey: ['support', 'client', clientId, month],
    queryFn: () => api.get<SupportClientDetail>(`/support/${clientId}`, { params: { month } }),
    enabled: Boolean(clientId),
  });
}

/**
 * Every client's bank — the same rows `/support` renders, and the authority
 * behind the dashboard's "Support hours at risk" card.
 *
 * ⚠ The dashboard's own payload says a bank is `low`; it does not say WHY,
 * and `low` has two causes with opposite remedies (the burn rate is about to
 * finish the balance, or a block expires and takes the whole balance with it
 * whatever the burn rate does). `exhaustionReason` lives here, so the card
 * reads it here rather than the dashboard inventing a second copy of it. The
 * card renders unchanged while this is loading or unavailable.
 */
export function useSupportBanks(enabled = true) {
  return useQuery({
    queryKey: ['support', 'banks'],
    queryFn: () => api.get<{ summary: SupportBankSummary; clients: SupportBankRow[] }>('/support/banks'),
    enabled,
  });
}

/** One client's bank: blocks, statement and the alerts it has fired. */
export function useSupportBank(clientId: string | null) {
  return useQuery({
    queryKey: ['support', 'bank', clientId],
    queryFn: () => api.get<SupportBankDetail>(`/support/banks/${clientId}`),
    enabled: Boolean(clientId),
  });
}

export function useBankAlerts() {
  return useQuery({
    queryKey: ['support', 'alerts'],
    queryFn: () => api.get<{ alerts: HourAlertRow[] }>('/support/banks/alerts'),
  });
}

export interface ManagementFeePolicy {
  /** The organization-wide floor, in hours. */
  hours: number;
  /** The built-in fallback, so a screen can say "default" without hardcoding it. */
  defaultHours: number;
  maxHours: number;
  clients: Record<string, { hours?: number; exempt?: boolean }>;
}

/** The org default plus every per-client override. One read, two screens. */
export function useManagementFeePolicy(enabled = true) {
  return useQuery({
    queryKey: ['support', 'management-fee'],
    queryFn: () => api.get<ManagementFeePolicy>('/support/management-fee'),
    enabled,
  });
}

export function useSetManagementFee() {
  const invalidate = useInvalidateSupport();
  return useMutation({
    mutationFn: (hours: number) =>
      api.put<ManagementFeePolicy>('/support/management-fee', { hours }),
    onSuccess: invalidate,
  });
}

/** One client's override: a different amount, or exempt. */
export function useSetClientManagementFee() {
  const invalidate = useInvalidateSupport();
  return useMutation({
    mutationFn: ({
      clientId,
      ...body
    }: {
      clientId: string;
      hours?: number | null;
      exempt?: boolean;
    }) => api.put<ManagementFeePolicy>(`/support/management-fee/clients/${clientId}`, body),
    onSuccess: invalidate,
  });
}

/** Undo one month's fee with a compensating fact — the row is never deleted. */
export function useReverseManagementFee() {
  const invalidate = useInvalidateSupport();
  return useMutation({
    mutationFn: ({ clientId, period }: { clientId: string; period: string }) =>
      api.post<{ ok: boolean; period: string; hours: number }>(
        '/support/management-fee/reverse',
        { clientId, period },
      ),
    onSuccess: invalidate,
  });
}

export function useAllowances(enabled: boolean) {
  return useQuery({
    queryKey: ['support', 'allowances'],
    queryFn: () => api.get<{ clients: AllowanceClient[] }>('/support/allowances'),
    enabled,
  });
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

export interface AllowanceInput {
  clientId: string;
  monthlyHours: number;
  effectiveFrom: string; // YYYY-MM-DD
  effectiveTo?: string | null;
}

function useInvalidateSupport() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: ['support'] });
}

export interface HourBlockInput {
  clientId: string;
  hours: number;
  rate: number;
  purchasedAt?: string;
  expiresAt?: string | null;
  source?: 'INVOICE' | 'STRIPE' | 'MANUAL';
  invoiceId?: string | null;
  note?: string | null;
}

/** Record a purchase of hours. It grants nothing until it is paid (or, for an invoice, sent). */
export function useCreateHourBlock() {
  const invalidate = useInvalidateSupport();
  return useMutation({
    mutationFn: (input: HourBlockInput) =>
      api.post<{ id: string; status: string }>('/support/banks/blocks', input),
    onSuccess: invalidate,
  });
}

export interface SellSupportHoursInput {
  clientId: string;
  agreementId?: string | null;
  hours: number;
  /** Omit to use the agreement's hourly rate, then the client's support rate. */
  rate?: number | null;
  expiresAt?: string | null;
  issueDate?: string | null;
  dueDate?: string | null;
  note?: string | null;
}

export interface SellSupportHoursResult {
  invoiceId: string;
  invoiceNumber: string;
  blockId: string;
  hours: number;
  rate: number;
  amount: number;
}

/**
 * Sell a block of support hours from an agreement: one DRAFT invoice and the
 * PENDING block behind it, which activates when the invoice is sent.
 */
export function useSellSupportHours() {
  const invalidate = useInvalidateSupport();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: SellSupportHoursInput) =>
      api.post<SellSupportHoursResult>('/support/banks/blocks/sell', input),
    onSuccess: () => {
      invalidate();
      void queryClient.invalidateQueries({ queryKey: ['invoices'] });
      void queryClient.invalidateQueries({ queryKey: ['clients'] });
    },
  });
}

/** A refund is a compensating ledger entry — a consumed block is never deleted. */
export function useRefundHourBlock() {
  const invalidate = useInvalidateSupport();
  return useMutation({
    mutationFn: ({ id, note }: { id: string; note?: string }) =>
      api.post<{ ok: boolean }>(`/support/banks/blocks/${id}/refund`, { note }),
    onSuccess: invalidate,
  });
}

export function useDeleteHourBlock() {
  const invalidate = useInvalidateSupport();
  return useMutation({
    mutationFn: (id: string) => api.delete<{ ok: boolean }>(`/support/banks/blocks/${id}`),
    onSuccess: invalidate,
  });
}

export function useAcknowledgeAlert() {
  const invalidate = useInvalidateSupport();
  return useMutation({
    mutationFn: (id: string) => api.post<{ ok: boolean }>(`/support/banks/alerts/${id}/acknowledge`, {}),
    onSuccess: invalidate,
  });
}

export function useCreateAllowance() {
  const invalidate = useInvalidateSupport();
  return useMutation({
    mutationFn: (input: AllowanceInput) => api.post<Allowance>('/support/allowances', input),
    onSuccess: invalidate,
  });
}

export function useUpdateAllowance() {
  const invalidate = useInvalidateSupport();
  return useMutation({
    mutationFn: ({ id, ...input }: Partial<AllowanceInput> & { id: string }) =>
      api.patch<Allowance>(`/support/allowances/${id}`, input),
    onSuccess: invalidate,
  });
}

export function useDeleteAllowance() {
  const invalidate = useInvalidateSupport();
  return useMutation({
    mutationFn: (id: string) => api.delete<{ ok: boolean }>(`/support/allowances/${id}`),
    onSuccess: invalidate,
  });
}
